<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { useAreas, useCategories } from '@ilteducation/content-ref-data';
import { useVideos } from '@ilteducation/videos';
import config from '@/config/ConfigProvider';
import { ref, watch } from 'vue';
import useSnackbar from '@/composables/use-snackbar';
import axios from 'axios';
import { useConfigHelper } from '@ilteducation/config';
import type { VideoV1 } from '@ilteducation/content-registry-types';

const route = useRoute();
const router = useRouter();
const { categories } = useCategories();
const { areas, isLoading: isLoadingAreas } = useAreas();
const videoStore = useVideos();
const { getApiUrl } = useConfigHelper();

const countryCode = config.value('countryCode') as string;

const videoId = ref(route.params.videoId?.toString());

async function getVideoById(uid: string) {
  const video = await videoStore.getVideoById(uid);
  return { videos: video.videos, slug: video.slug };
}

async function getVideoByFilmId(filmId: string) {
  const { data: video } = await axios.get<VideoV1>(`${getApiUrl('content-registry', true)}/legacy/Video/all/${filmId}`);
  return {
    videos: video.videos,
    slug: video.slug[countryCode as keyof VideoV1['slug']] || '',
  };
}

watch(
  [videoId, isLoadingAreas],
  async ([, isLoading]) => {
    if (isLoading) {
      return;
    }
    try {
      if (!videoId.value) {
        throw new Error('Missing video id.');
      }

      let data: { videos: VideoV1['videos']; slug: string } | undefined;
      try {
        data = await getVideoById(videoId.value);
      } catch {
        try {
          data = await getVideoByFilmId(videoId.value);
        } catch {
          // ignore
        }
      }
      if (!data || !data.videos || !data.slug) {
        throw new Error(`Could not find the video: ${videoId.value}`);
      }

      const fileAvailability = data.videos?.find((v) => v.market === countryCode);
      if (!fileAvailability) {
        throw new Error('The video is not available in this country.');
      }

      const category = categories.value.find((c) => c.id === fileAvailability?.categoryId);
      if (!category) {
        throw new Error(`Could not find the subject with id: ${fileAvailability.categoryId}`);
      }

      const area = areas.value.find((a) => a.id === fileAvailability?.areaId);
      if (!area) {
        throw new Error(`Could not find the area with id: ${fileAvailability.areaId}`);
      }

      router.push({ name: 'showFilm', params: { film: data.slug, area: area.slug, subject: category.slug } });
    } catch (error) {
      const err = error as Error;
      if ('message' in err) {
        const { snackbarError } = useSnackbar();
        snackbarError(err.message);
      }
      router.push({ name: 'begreppaLanding' });
    }
  },
  { immediate: true },
);
</script>

<template>
  <div></div>
</template>
